import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import { AsyncPollerService } from 'services/asyncPoller';
import { PollingService } from 'services/Polling/Polling';
import type { APIList, StringifiedUUID } from 'types';
import {
  PlanAccessResponse,
  PlanningCycle,
  PlanningCycleRequest,
  PlanningCycleSnapshotUpsertRequest,
  PlanningCycleSnapshotUpsertResponse,
} from 'types/planningCycles';
import { SPMWorksheetInterface } from 'types/SPM';
import { GetSnapshotResponse } from 'types/worksheets';
import type { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

const apiUrl = '/api/v1/tq/planning-cycles/';
const planningCycleResourceSyncUrl = '/api/v1/resource-sync/planning_cycle/';
const syncToParentQueryParams = { src: 'child', dest: 'parent' };
const SYNC_TO_PARENT_LOCKOUT_TIMEOUT_MS = 5 * 60 * 1000;

const getPlanningCycle = (planningCycleId: StringifiedUUID) =>
  getRequest<null, PlanningCycle>(`${apiUrl}${planningCycleId}/`);

const useGetPlanningCyclesQuery = (planningCycleId: StringifiedUUID) => {
  const QUERY_KEY = ['planning-cycles', planningCycleId];
  const queryClient = useQueryClient();

  const query = useQuery<PlanningCycle, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getPlanningCycle(planningCycleId),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

const getPlanAccess = () => getRequest<null, PlanAccessResponse>(`${apiUrl}plan-access/`);

const useGetPlanAccessQuery = (options: QueryHookOptions<PlanAccessResponse, APIError> = {}) => {
  const QUERY_KEY = ['planning-cycles', 'plan-access'];
  const query = useQuery<PlanAccessResponse, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getPlanAccess(),
  });

  return { query };
};

const listPlanningCycles = () => getRequest<null, APIList<PlanningCycle>>(`${apiUrl}?limit=100`);

const useListPlanningCyclesQuery = (
  options: QueryHookOptions<APIList<PlanningCycle>, APIError, PlanningCycle[]> = {},
) => {
  const QUERY_KEY = ['planning-cycles', 'list'];
  const queryClient = useQueryClient();
  const query = useQuery<APIList<PlanningCycle>, APIError, PlanningCycle[]>({
    select: (response) => response.data || [],
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => listPlanningCycles(),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

const createPlanningCycle = (csrfToken: string) => (planningCycle: PlanningCycleRequest) => {
  return postRequest<PlanningCycleRequest, PlanningCycle>(apiUrl, planningCycle, csrfToken);
};

const useCreatePlanningCycleMutation = (
  csrfToken: string,
  options: UseMutationOptions<PlanningCycle, APIError, PlanningCycleRequest> = {},
) => {
  return {
    mutation: useMutation<PlanningCycle, APIError, PlanningCycleRequest>({
      ...options,
      mutationFn: createPlanningCycle(csrfToken),
    }),
  };
};

type ClonePlanRequest = {
  name: string;
};

type ClonePlanResponse = {
  aggregation_id: StringifiedUUID;
};

const clonePlanningCycle = (csrfToken: string, planningCycleId: StringifiedUUID) => (payload: ClonePlanRequest) => {
  return postRequest<ClonePlanRequest, ClonePlanResponse>(`${apiUrl}${planningCycleId}/clone/`, payload, csrfToken);
};

const useClonePlanningCycleMutation = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  options: UseMutationOptions<ClonePlanResponse, APIError, ClonePlanRequest> = {},
) => {
  return {
    mutation: useMutation<ClonePlanResponse, APIError, ClonePlanRequest>({
      ...options,
      mutationFn: clonePlanningCycle(csrfToken, planningCycleId),
    }),
  };
};

// can we consider not using strict serializer on backend here to save having to split up the payload
type UpdatePlanningCycleProps = {
  planningCycleId: StringifiedUUID;
  planningCycle: PlanningCycleRequest;
};

const updatePlanningCycle =
  (csrfToken: string) =>
  ({ planningCycleId, planningCycle }: UpdatePlanningCycleProps) => {
    return putRequest<PlanningCycleRequest, PlanningCycle>(`${apiUrl}${planningCycleId}/`, planningCycle, csrfToken);
  };

const useUpdatePlanningCycleMutation = (
  csrfToken: string,
  options: UseMutationOptions<PlanningCycle, APIError, UpdatePlanningCycleProps> = {},
) => {
  return {
    mutation: useMutation<PlanningCycle, APIError, UpdatePlanningCycleProps>({
      ...options,
      mutationFn: updatePlanningCycle(csrfToken),
    }),
  };
};

type UpdatePlanningCycleDatesProps = {
  planningCycleId: StringifiedUUID;
  planning_cycle_dates: string[];
};

const updatePlanningCycleDates =
  (csrfToken: string) =>
  ({ planningCycleId, planning_cycle_dates }: UpdatePlanningCycleDatesProps) => {
    return putRequest<{ planning_cycle_dates: string[] }, PlanningCycle>(
      `${apiUrl}${planningCycleId}/planning-cycle-dates/`,
      { planning_cycle_dates },
      csrfToken,
    );
  };

const useUpdatePlanningCycleDatesMutation = (
  csrfToken: string,
  options: UseMutationOptions<PlanningCycle, APIError, UpdatePlanningCycleDatesProps> = {},
) => {
  return {
    mutation: useMutation<PlanningCycle, APIError, UpdatePlanningCycleDatesProps>({
      ...options,
      mutationFn: updatePlanningCycleDates(csrfToken),
    }),
  };
};

const deletePlanningCycle = (csrfToken: string) => (planningCycleId: StringifiedUUID) => {
  return deleteRequest<null, PlanningCycle>(`${apiUrl}${planningCycleId}/`, null, csrfToken);
};

const useDeletePlanningCycleMutation = (
  csrfToken: string,
  options: UseMutationOptions<PlanningCycle, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<PlanningCycle, APIError, StringifiedUUID>({
      ...options,
      mutationFn: deletePlanningCycle(csrfToken),
    }),
  };
};

const upsertDataSnapshotReference = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  payload: PlanningCycleSnapshotUpsertRequest,
) => {
  return postRequest<PlanningCycleSnapshotUpsertRequest, PlanningCycleSnapshotUpsertResponse>(
    `${apiUrl}${planningCycleId}/data-snapshot-reference/`,
    payload,
    csrfToken,
  );
};

const syncDataSnapshotReference = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  payload: PlanningCycleSnapshotUpsertRequest,
) => {
  return postRequest<PlanningCycleSnapshotUpsertRequest, GetSnapshotResponse>(
    `${apiUrl}${planningCycleId}/data-snapshot-sync/`,
    payload,
    csrfToken,
  );
};

const getDataSnapshotStatus = (planningCycleId: StringifiedUUID) => {
  return getRequest<null, GetSnapshotResponse>(`${apiUrl}${planningCycleId}/data-snapshot-reference/`);
};

type GetPlanningCycleSPMWorksheetsParams = {
  planningCycleId: StringifiedUUID;
  /** When set, response includes TQ data object schemas (accounts / territories) for the scenario. */
  scenarioId?: StringifiedUUID;
};

const getPlanningCycleSPMWorksheets = ({ planningCycleId, scenarioId }: GetPlanningCycleSPMWorksheetsParams) => {
  const qs = scenarioId ? `?scenario_id=${encodeURIComponent(scenarioId)}` : '';
  return getRequest<null, APIList<SPMWorksheetInterface>>(
    `${apiUrl}${planningCycleId}/connected-modeling-worksheets/${qs}`,
  );
};

type UseGetPlanningCycleSPMWorksheetsQueryOptions = QueryHookOptions<APIList<SPMWorksheetInterface>, APIError> & {
  scenarioId?: StringifiedUUID;
};

const useGetPlanningCycleSPMWorksheetsQuery = (
  planningCycleId: StringifiedUUID,
  options: UseGetPlanningCycleSPMWorksheetsQueryOptions = {},
) => {
  const { scenarioId, ...queryOptions } = options;
  // todo: consider moving this api to spm worksheets service
  const QUERY_KEY = ['planning-cycles', planningCycleId, 'connected-modeling-worksheets', scenarioId ?? 'none'];
  const queryClient = useQueryClient();
  const query = useQuery<APIList<SPMWorksheetInterface>, APIError>({
    ...queryOptions,
    queryKey: QUERY_KEY,
    queryFn: () => getPlanningCycleSPMWorksheets({ planningCycleId, scenarioId }),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

type SyncToParentTargetResponse = {
  name: string | null;
};

const getSyncToParentTarget = (planningCycleId: StringifiedUUID) =>
  getRequest<null, SyncToParentTargetResponse>(`${apiUrl}${planningCycleId}/sync-to-parent-target/`);

const useGetSyncToParentTargetQuery = (
  planningCycleId: StringifiedUUID,
  options: QueryHookOptions<SyncToParentTargetResponse, APIError> = {},
) => {
  const QUERY_KEY = ['planning-cycles', planningCycleId, 'sync-to-parent-target'];
  const query = useQuery<SyncToParentTargetResponse, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getSyncToParentTarget(planningCycleId),
  });

  return { query };
};

const cacheSyncToParentLockout = (csrfToken: string, planningCycleId: StringifiedUUID) =>
  new Promise<void>((resolve, reject) => {
    const pollerName = `sync-to-parent-${planningCycleId}-${Date.now()}`;
    const summaryOfChangesUrl = `${planningCycleResourceSyncUrl}${planningCycleId}/summary-of-changes/`;
    const timeoutId = window.setTimeout(() => {
      PollingService.terminate(pollerName);
      reject(new Error('Timed out preparing the sync to parent'));
    }, SYNC_TO_PARENT_LOCKOUT_TIMEOUT_MS);
    AsyncPollerService.start(
      csrfToken,
      pollerName,
      `${summaryOfChangesUrl}async/`,
      { ...syncToParentQueryParams, bypass_merge_conflict: true },
      (result: { object?: string; message?: string; error?: Error } | null, error?: Error) => {
        window.clearTimeout(timeoutId);
        if (error || result?.error) {
          reject(error ?? result?.error);
        } else if (!result || result.object === 'error') {
          reject(new Error(result?.message));
        } else {
          resolve();
        }
      },
      `${summaryOfChangesUrl}cancel-async/`,
    );
  });

const syncToParent = async (csrfToken: string, planningCycleId: StringifiedUUID) => {
  await cacheSyncToParentLockout(csrfToken, planningCycleId);
  const query = new URLSearchParams(syncToParentQueryParams).toString();
  return postRequest<Record<string, never>, unknown>(
    `${planningCycleResourceSyncUrl}${planningCycleId}/confirm-merge/?${query}`,
    {},
    csrfToken,
  );
};

export const PlanningCyclesService = {
  apiUrl,
  useGetPlanningCyclesQuery,
  useGetPlanAccessQuery,
  useListPlanningCyclesQuery,
  useGetPlanningCycleSPMWorksheetsQuery,
  useCreatePlanningCycleMutation,
  useUpdatePlanningCycleMutation,
  useUpdatePlanningCycleDatesMutation,
  useDeletePlanningCycleMutation,
  useClonePlanningCycleMutation,
  upsertDataSnapshotReference,
  syncDataSnapshotReference,
  getDataSnapshotStatus,
  useGetSyncToParentTargetQuery,
  syncToParent,
};
